import React, { FC, MouseEvent, useRef, useState } from "react"
import {
  useFloating,
  autoUpdate,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  safePolygon,
  FloatingPortal,
  offset,
  flip,
  arrow,
  FloatingArrow
} from "@floating-ui/react"
import { useAppDispatch } from "../../hooks"
import { pushToast } from "../../../redux/slices/notifications"
import { copyToClipboard } from "../../utils"

export type Alignment = "start" | "end"
export type Side = "top" | "right" | "bottom" | "left"
export type AlignedPlacement = `${Side}-${Alignment}`

export interface Props {
  toolTipNode: React.ReactElement
  text: React.ReactNode
  displayTextClass?: string
  placement?: Side | AlignedPlacement
  open?: boolean
  copyable?: boolean
}

const Tooltip: FC<Props> = ({
  toolTipNode,
  text,
  placement,
  displayTextClass = "",
  open: controlledOpen,
  copyable = false
}) => {
  const dispatch = useAppDispatch()
  const [internalOpen, setIsOpen] = useState(false)
  const isOpen = controlledOpen ?? internalOpen
  const arrowRef = useRef(null)

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: placement ?? "top",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({
        fallbackAxisSideDirection: "start"
      }),
      shift(),
      arrow({
        element: arrowRef
      })
    ]
  })

  const hover = useHover(context, {
    move: false,
    handleClose: copyable ? safePolygon() : undefined
  })
  const focus = useFocus(context)
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: copyable ? "dialog" : "tooltip" })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ])

  const handleCopy = async (e: MouseEvent<HTMLDivElement>) => {
    if (!copyable || typeof text !== "string") return
    e.stopPropagation()

    try {
      await copyToClipboard(text)
      dispatch(
        pushToast({
          msg: "Copied to clipboard",
          theme: "success",
          timeout: 2000
        })
      )
    } catch {
      dispatch(
        pushToast({
          msg: "Failed to copy to clipboard",
          theme: "danger",
          timeout: 2000
        })
      )
    }
  }

  return (
    <>
      <div
        className={`truncate ${displayTextClass}`}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {toolTipNode}
      </div>
      <FloatingPortal>
        {isOpen && (
          <div
            className={`z-50 max-w-[10rem] md:max-w-xs bg-[#42405A] rounded-md text-neutral-50 text-xs md:text-sm p-2 break-all break-words ${
              copyable ? "cursor-pointer" : ""
            }`}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            onClick={handleCopy}
            title={copyable ? "Click to copy" : undefined}
          >
            <FloatingArrow ref={arrowRef} context={context} fill="#42405A" />
            {text}
          </div>
        )}
      </FloatingPortal>
    </>
  )
}

export default Tooltip
